/**
 * P2 — emitted-URL resolution rail.
 *
 * ## The defect this exists for
 *
 * The SEO factory publishes URLs on the site's behalf: the `logo` and `image`
 * IRIs in the `@graph`, `og:image` / `twitter:image`, favicon and touch-icon
 * links, `<loc>`s in `sitemap.xml`, and the page links in `llms.txt`. Nothing
 * has ever asserted that any of them resolve.
 *
 * Measured on live production (2026-07-27):
 *
 * ```
 * GET /logos/iron-oak-mark-light.svg   200  text/html  67,532 bytes
 * GET /                                200  text/html  67,532 bytes
 * sha256(both) = e83f660fc39e3ec2…                 ← byte-identical
 * ```
 *
 * `Organization.logo` and `HomeAndConstructionBusiness.logo` on **every route**
 * of the fleet's flagship point at a file that does not exist. There is no
 * `site/public/logos/` directory in the repo at all. The miss is invisible
 * because Azure Static Web Apps' `navigationFallback` answers any unmatched path
 * with `index.html` and a 200 — so a knowledge-graph entity's logo is a web
 * page, no 404 is ever logged, and no console error is ever raised. `dcs-seo`
 * calls this shape "worse than a 404".
 *
 * ## The two checks, and why they are split
 *
 * **Same-origin assets are checked against `dist/` — no network at all.** If the
 * factory emitted `https://site.com/logos/x.svg` and `dist/logos/x.svg` does not
 * exist, then the host has nothing to serve and its SPA fallback WILL answer
 * HTML. That inference is exact, deterministic, offline, and instant, so it is a
 * hard build failure. This is the check that catches the Iron Oak defect.
 *
 * **Cross-origin assets need the network, so they are designed not to flake.**
 * A CDN URL can only be verified by asking. The policy is deliberately
 * asymmetric:
 *   - a *definitive* answer that is wrong (HTML where an image was promised, or
 *     a 4xx/5xx) is a HARD FAILURE — that is the defect, and the answer is not
 *     in doubt;
 *   - a *non-answer* (DNS failure, timeout, connection reset, exhausted budget)
 *     is a WARNING and never fails the build.
 *
 * That asymmetry is the whole point. A gate that reds when someone's wifi
 * hiccups gets disabled within a week and then protects nothing — the exact
 * vacuous-green failure mode this campaign keeps finding. Results are cached
 * with a TTL and the whole pass runs under a total time budget, so the marginal
 * cost on a warm CI run is ~zero.
 */

import fs from 'node:fs'
import path from 'node:path'
import { readTagAttr } from './headHonesty'

/** What kind of resource a URL is expected to return. */
export type UrlExpectation =
  /** Must NOT be HTML — an image, icon, font, or text/xml artifact. */
  | 'asset'
  /** An HTML page (canonical, sitemap entry, llms.txt page link). */
  | 'page'
  /** An identifier or a third-party profile — never fetched. */
  | 'skip'

/** One URL the factory emitted, with where it came from. */
export interface EmittedUrl {
  /** The URL exactly as emitted. */
  url: string
  /** Human-readable provenance, e.g. `JSON-LD Organization.logo`. */
  source: string
  /** The emitted file this came from (`/services`, `sitemap.xml`, …). */
  from: string
  expectation: UrlExpectation
}

/** A URL that resolved to the wrong thing. */
export interface EmittedUrlViolation {
  url: string
  source: string
  from: string
  reason: string
}

/** A URL we could not get a definitive answer about. */
export interface EmittedUrlWarning {
  url: string
  source: string
  from: string
  reason: string
}

// -----------------------------------------------------------------------------
// Collection
// -----------------------------------------------------------------------------

/** Extensions that are definitionally not HTML documents. */
const NON_HTML_EXTENSIONS = new Set([
  '.svg', '.png', '.jpg', '.jpeg', '.webp', '.avif', '.gif', '.ico', '.bmp',
  '.woff', '.woff2', '.ttf', '.otf', '.eot',
  '.txt', '.xml', '.json', '.pdf', '.mp4', '.webm', '.mp3', '.wav',
  '.css', '.js', '.mjs', '.map',
])

/** JSON-LD keys whose values are asset IRIs (must not be HTML). */
const JSONLD_ASSET_KEYS = new Set([
  'logo', 'image', 'contentUrl', 'thumbnailUrl', 'photo', 'primaryImageOfPage',
])

/** JSON-LD keys whose values are page IRIs. */
const JSONLD_PAGE_KEYS = new Set(['url', 'item', 'mainEntityOfPage'])

/** `<link rel>` values that point at an asset. */
const ASSET_LINK_RELS = new Set([
  'icon', 'shortcut icon', 'apple-touch-icon', 'apple-touch-icon-precomposed',
  'mask-icon', 'manifest', 'preload',
])

/** `<link rel>` values that point at a page. */
const PAGE_LINK_RELS = new Set(['canonical', 'alternate'])

function extensionOf(url: string): string {
  try {
    const u = url.startsWith('http') ? new URL(url) : new URL(url, 'https://placeholder.invalid')
    return path.extname(u.pathname).toLowerCase()
  } catch {
    return ''
  }
}

/** Recursively pull asset/page IRIs out of a parsed JSON-LD value. */
function walkJsonLd(
  node: unknown,
  from: string,
  trail: string,
  out: EmittedUrl[]
): void {
  if (Array.isArray(node)) {
    for (const item of node) walkJsonLd(item, from, trail, out)
    return
  }
  if (node === null || typeof node !== 'object') return

  // Name the node by its @type so a violation reads `JSON-LD Organization.logo`
  // rather than an anonymous path — the operator needs to know WHICH entity in
  // the @graph published the bad URL.
  const nodeType = (node as Record<string, unknown>)['@type']
  const scope = typeof nodeType === 'string' ? `${trail}${nodeType}.` : trail

  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    // `@id` is an IDENTIFIER, not a locator — schema.org node IRIs carry
    // `#organization` fragments and are not required to resolve. Fetching them
    // would manufacture noise. `sameAs` points at third-party profiles
    // (Facebook, Yelp) that bot-block and rate-limit; asserting on them would
    // make this rail flaky for reasons that are not the site's fault.
    if (key === '@id' || key === 'sameAs' || key === '@context' || key === '@type') continue

    if (typeof value === 'string' && /^(https?:)?\/\//.test(value)) {
      if (JSONLD_ASSET_KEYS.has(key)) {
        out.push({ url: value, source: `JSON-LD ${scope}${key}`, from, expectation: 'asset' })
        continue
      }
      if (JSONLD_PAGE_KEYS.has(key)) {
        out.push({ url: value, source: `JSON-LD ${scope}${key}`, from, expectation: 'page' })
        continue
      }
      continue
    }
    if (Array.isArray(value) && JSONLD_ASSET_KEYS.has(key)) {
      value.forEach((v, i) => {
        if (typeof v === 'string') {
          out.push({ url: v, source: `JSON-LD ${scope}${key}[${i}]`, from, expectation: 'asset' })
        } else {
          walkJsonLd(v, from, `${scope}${key}[${i}].`, out)
        }
      })
      continue
    }
    if (value !== null && typeof value === 'object') {
      // Arrays keep the current scope; a nested object names itself from its own
      // @type when it descends (handled at the top of this function).
      walkJsonLd(value, from, Array.isArray(value) ? scope : `${scope}${key}.`, out)
    }
  }
}

/**
 * Undo the `escapeJsonLd` transform before parsing. The emitter writes `<`, `>`
 * and `&` as `<`/`>`/`&` inside the script body, which `JSON.parse`
 * already handles — so this only strips the HTML-attribute escaping applied to
 * plain meta/link values.
 */
function unescapeAttr(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}

/**
 * Collect every URL an emitted HTML document publishes: OG/Twitter images,
 * asset and page `<link>`s, and every asset/page IRI inside its JSON-LD blocks.
 */
export function collectUrlsFromHtml(html: string, from: string): EmittedUrl[] {
  const out: EmittedUrl[] = []

  const metaRe = /<meta\b[^>]*>/gi
  let m: RegExpExecArray | null
  while ((m = metaRe.exec(html)) !== null) {
    const tag = m[0]
    // Delimiter-aware: a `["']…["']` pair terminates on the first quote OR
    // apostrophe, which silently truncates any value containing one. See
    // `readTagAttr` in headHonesty.ts for the measured consequence.
    const property = readTagAttr(tag, 'property')?.toLowerCase()
    const name = readTagAttr(tag, 'name')?.toLowerCase()
    const content = readTagAttr(tag, 'content')
    if (!content) continue
    const key = property ?? name
    if (!key) continue
    if (key === 'og:image' || key === 'twitter:image' || key === 'og:image:secure_url') {
      out.push({ url: unescapeAttr(content), source: key, from, expectation: 'asset' })
    } else if (key === 'og:url') {
      out.push({ url: unescapeAttr(content), source: key, from, expectation: 'page' })
    }
  }

  const linkRe = /<link\b[^>]*>/gi
  while ((m = linkRe.exec(html)) !== null) {
    const tag = m[0]
    const rel = readTagAttr(tag, 'rel')?.toLowerCase()
    const href = readTagAttr(tag, 'href')
    if (!rel || !href) continue
    if (ASSET_LINK_RELS.has(rel)) {
      out.push({ url: unescapeAttr(href), source: `link rel="${rel}"`, from, expectation: 'asset' })
    } else if (PAGE_LINK_RELS.has(rel)) {
      out.push({ url: unescapeAttr(href), source: `link rel="${rel}"`, from, expectation: 'page' })
    }
  }

  const scriptRe = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi
  while ((m = scriptRe.exec(html)) !== null) {
    try {
      walkJsonLd(JSON.parse(m[1]), from, '', out)
    } catch {
      // A JSON-LD block that does not parse is its own defect, already covered by
      // the seo-guardrail; this rail only reports on URLs it can actually read.
    }
  }

  return out
}

/** Collect `<loc>` entries from an emitted sitemap. */
export function collectUrlsFromSitemap(xml: string, from = 'sitemap.xml'): EmittedUrl[] {
  const out: EmittedUrl[] = []
  const re = /<loc>\s*([\s\S]*?)\s*<\/loc>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(xml)) !== null) {
    out.push({ url: unescapeAttr(m[1].trim()), source: '<loc>', from, expectation: 'page' })
  }
  return out
}

/** Collect the markdown link targets from an emitted `llms.txt`. */
export function collectUrlsFromLlmsTxt(txt: string, from = 'llms.txt'): EmittedUrl[] {
  const out: EmittedUrl[] = []
  const re = /\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(txt)) !== null) {
    const url = m[1]
    out.push({
      url,
      source: 'llms.txt link',
      from,
      // An llms.txt entry may point at a page OR at a text artifact
      // (`llms-full.txt`); classify by extension so a `.txt` reference is held to
      // the non-HTML bar — that is exactly the shape an answer engine probes for.
      expectation: NON_HTML_EXTENSIONS.has(extensionOf(url)) ? 'asset' : 'page',
    })
  }
  return out
}

/**
 * Read the emitted artifacts off disk and collect every URL they publish.
 *
 * Takes an explicit file list (built by the caller from the same route manifest
 * the emitter used) rather than walking `dist/`, so the rail only ever inspects
 * files the factory actually wrote — never a stray asset or a stale artifact
 * from a previous build.
 */
export function collectEmittedUrlsFromFiles(
  files: Array<{ path: string; from: string; kind: 'html' | 'sitemap' | 'llms' }>
): EmittedUrl[] {
  const out: EmittedUrl[] = []
  for (const entry of files) {
    let text: string
    try {
      text = fs.readFileSync(entry.path, 'utf8')
    } catch {
      continue // not emitted for this build (e.g. preview skips sitemap/llms)
    }
    if (entry.kind === 'html') out.push(...collectUrlsFromHtml(text, entry.from))
    else if (entry.kind === 'sitemap') out.push(...collectUrlsFromSitemap(text, entry.from))
    else out.push(...collectUrlsFromLlmsTxt(text, entry.from))
  }
  return out
}

/** De-duplicate by URL+expectation, keeping the first provenance seen. */
export function dedupeUrls(urls: EmittedUrl[]): EmittedUrl[] {
  const seen = new Map<string, EmittedUrl>()
  for (const u of urls) {
    const key = `${u.expectation} ${u.url}`
    if (!seen.has(key)) seen.set(key, u)
  }
  return [...seen.values()]
}

// -----------------------------------------------------------------------------
// Classification
// -----------------------------------------------------------------------------

/** How a URL relates to the site being built. */
export type UrlLocality = 'same-origin' | 'remote' | 'unfetchable'

/** The parsed form of an emitted URL. */
export interface ClassifiedUrl extends EmittedUrl {
  locality: UrlLocality
  /** Path portion, for same-origin resolution against `dist/`. */
  pathname: string
}

/**
 * Decide whether a URL points back at the site we just built (so it can be
 * proven against `dist/` offline) or somewhere else (so it needs the network).
 *
 * Protocol-relative and root-relative URLs are treated as same-origin, matching
 * how a browser resolves them from the emitted document.
 */
export function classifyUrl(entry: EmittedUrl, siteUrl: string | undefined): ClassifiedUrl {
  const url = entry.url.trim()
  if (/^(data|mailto|tel|javascript|blob):/i.test(url)) {
    return { ...entry, locality: 'unfetchable', pathname: '' }
  }

  let siteOrigin: string | undefined
  try {
    if (siteUrl) siteOrigin = new URL(siteUrl).origin
  } catch {
    siteOrigin = undefined
  }

  if (url.startsWith('/') && !url.startsWith('//')) {
    return { ...entry, locality: 'same-origin', pathname: url.split(/[?#]/)[0] }
  }

  try {
    const parsed = new URL(url.startsWith('//') ? `https:${url}` : url)
    if (siteOrigin && parsed.origin === siteOrigin) {
      return { ...entry, locality: 'same-origin', pathname: parsed.pathname }
    }
    return { ...entry, locality: 'remote', pathname: parsed.pathname }
  } catch {
    return { ...entry, locality: 'unfetchable', pathname: '' }
  }
}

// -----------------------------------------------------------------------------
// Same-origin check (fs only — deterministic, offline, hard-fail)
// -----------------------------------------------------------------------------

/**
 * Resolve a same-origin pathname to the file that would serve it from `dist/`.
 * Honours a non-root Vite `base` (subpath preview deploys) by trying the
 * base-stripped path as well.
 */
function resolveDistCandidates(outDir: string, pathname: string, base: string): string[] {
  const clean = decodeURIComponent(pathname).replace(/^\/+/, '')
  const candidates = [path.join(outDir, ...clean.split('/'))]
  const normalizedBase = base.replace(/^\/+|\/+$/g, '')
  if (normalizedBase && clean.startsWith(normalizedBase + '/')) {
    candidates.push(path.join(outDir, ...clean.slice(normalizedBase.length + 1).split('/')))
  }
  return candidates
}

/**
 * Assert that every same-origin ASSET URL the factory emitted has a real file in
 * `dist/`.
 *
 * This is the check that catches the Iron Oak logo IRI, and it does so with no
 * network, no browser, and no ambiguity: an emitted `https://site/logos/x.svg`
 * with no `dist/logos/x.svg` behind it means the host's SPA fallback answers the
 * crawler with `index.html`.
 */
export function checkSameOriginAssets(params: {
  urls: ClassifiedUrl[]
  outDir: string
  base?: string
  allow?: string[]
}): EmittedUrlViolation[] {
  return auditSameOriginAssets(params).violations
}

/** What the same-origin half actually did — the numbers behind its claim. */
export interface SameOriginAuditResult {
  violations: EmittedUrlViolation[]
  /** Same-origin ASSET URLs actually resolved against `dist/`. */
  checked: number
  /** Same-origin asset URLs skipped because they are on the allow-list. */
  exempted: number
}

/**
 * The counting form of {@link checkSameOriginAssets}.
 *
 * C-338: the caller used to report `classified.length` ("N published URL(s)
 * verified") while this function only ever looks at `expectation === 'asset'`.
 * Page URLs — every sitemap `<loc>`, every canonical, every llms.txt page link —
 * were counted as verified without being touched. The claim must never be
 * broader than the check, so the check now returns its own denominator.
 */
export function auditSameOriginAssets(params: {
  urls: ClassifiedUrl[]
  outDir: string
  base?: string
  allow?: string[]
}): SameOriginAuditResult {
  const { urls, outDir, base = '/', allow = [] } = params
  const allowed = new Set(allow)
  const violations: EmittedUrlViolation[] = []
  let checked = 0
  let exempted = 0

  // De-duplicate here as well as at the call site: the same logo IRI appears on
  // every route and inside several @graph nodes, and one missing file must be
  // reported ONCE — a wall of identical lines is how a real finding gets skimmed.
  for (const entry of dedupeUrls(urls) as ClassifiedUrl[]) {
    if (entry.locality !== 'same-origin' || entry.expectation !== 'asset') continue
    if (allowed.has(entry.url) || allowed.has(entry.pathname)) {
      exempted += 1
      continue
    }

    checked += 1
    const candidates = resolveDistCandidates(outDir, entry.pathname, base)
    const found = candidates.some((c) => {
      try {
        return fs.statSync(c).isFile()
      } catch {
        return false
      }
    })
    if (found) continue

    violations.push({
      url: entry.url,
      source: entry.source,
      from: entry.from,
      reason:
        `no file at ${candidates.map((c) => path.relative(outDir, c)).join(' or ')} in the build output — ` +
        `the host's SPA fallback will answer this with index.html (200 text/html), so every ` +
        `crawler resolving it gets a web page where a non-HTML asset was promised`,
    })
  }

  return { violations, checked, exempted }
}

// -----------------------------------------------------------------------------
// Remote check (bounded, cached, WARN-on-nonanswer)
// -----------------------------------------------------------------------------

/** One cached probe result. */
interface CachedProbe {
  /** `ok` | `html` | `status` — a definitive verdict worth remembering. */
  verdict: 'ok' | 'html' | 'status'
  status?: number
  contentType?: string
  /** ms epoch. */
  at: number
}

interface ProbeCacheFile {
  version: 1
  entries: Record<string, CachedProbe>
}

const CACHE_VERSION = 1

function readCache(cacheFile: string): ProbeCacheFile {
  try {
    const parsed = JSON.parse(fs.readFileSync(cacheFile, 'utf8')) as ProbeCacheFile
    if (parsed?.version === CACHE_VERSION && parsed.entries) return parsed
  } catch {
    /* cold cache */
  }
  return { version: CACHE_VERSION, entries: {} }
}

function writeCache(cacheFile: string, cache: ProbeCacheFile): void {
  try {
    fs.mkdirSync(path.dirname(cacheFile), { recursive: true })
    fs.writeFileSync(cacheFile, JSON.stringify(cache), 'utf8')
  } catch {
    // A cache we cannot persist just means the next build re-probes. Never fatal.
  }
}

/** Options for the network half of the rail. */
export interface RemoteCheckOptions {
  /** Per-request timeout. Default 5000ms. */
  timeoutMs?: number
  /** Total wall-clock budget for the whole pass. Default 20000ms. */
  budgetMs?: number
  /** Parallel requests. Default 6. */
  concurrency?: number
  /** Cache file path. Default `<projectRoot>/node_modules/.cache/dcs-seo/url-probes.json`. */
  cacheFile?: string
  /** Cache TTL. Default 24h. */
  cacheTtlMs?: number
  /** URLs exempted from the check (still logged). */
  allow?: string[]
  /** Injectable for tests — defaults to global `fetch`. */
  fetchImpl?: typeof fetch
  debug?: boolean
}

/** Outcome of the remote pass. */
export interface RemoteCheckResult {
  violations: EmittedUrlViolation[]
  warnings: EmittedUrlWarning[]
  /** How many probes were served from cache (0 network cost). */
  cached: number
  /** How many probes hit the network. */
  probed: number
  /**
   * Remote ASSET URLs this pass was asked to check (`cached + probed +
   * unanswered`). The honest denominator for the build log — see
   * {@link auditSameOriginAssets} for why the count must come from the checker.
   */
  checked?: number
  /** Remote asset URLs skipped because they are on the allow-list. */
  exempted?: number
}

function isHtmlContentType(contentType: string | null | undefined): boolean {
  if (!contentType) return false
  return /^\s*(text\/html|application\/xhtml\+xml)/i.test(contentType)
}

/**
 * Probe every REMOTE asset URL and report only what the network told us
 * definitively.
 *
 * The verdict table is the contract:
 * | observation                                | outcome  | why |
 * |--------------------------------------------|----------|-----|
 * | 2xx, non-HTML content-type                 | pass     | the promise held |
 * | 2xx, `text/html`                           | **FAIL** | the exact silent-degradation shape |
 * | 4xx / 5xx                                  | **FAIL** | a definitive "this does not exist" |
 * | DNS/timeout/reset/abort, budget exhausted  | warn     | we learned nothing; never red on that |
 */
export async function checkRemoteAssets(
  urls: ClassifiedUrl[],
  options: RemoteCheckOptions & { projectRoot: string }
): Promise<RemoteCheckResult> {
  const {
    projectRoot,
    timeoutMs = 5000,
    budgetMs = 20000,
    concurrency = 6,
    cacheTtlMs = 24 * 60 * 60 * 1000,
    allow = [],
    fetchImpl = globalThis.fetch,
    debug = false,
  } = options
  const cacheFile =
    options.cacheFile ?? path.join(projectRoot, 'node_modules', '.cache', 'dcs-seo', 'url-probes.json')

  const allowed = new Set(allow)
  const remoteAssets = dedupeUrls(
    urls.filter((u) => u.locality === 'remote' && u.expectation === 'asset')
  ) as ClassifiedUrl[]
  const targets = remoteAssets.filter((u) => !allowed.has(u.url))
  const exempted = remoteAssets.length - targets.length

  const violations: EmittedUrlViolation[] = []
  const warnings: EmittedUrlWarning[] = []
  let cachedCount = 0
  let probedCount = 0
  /** Targets that got a DEFINITIVE answer — the only ones that may be claimed. */
  let answeredCount = 0

  if (targets.length === 0) {
    return { violations, warnings, cached: 0, probed: 0, checked: 0, exempted }
  }
  if (typeof fetchImpl !== 'function') {
    warnings.push({
      url: '(all remote)',
      source: 'remote probe',
      from: '-',
      reason: 'no global fetch available in this Node runtime; remote URL checks skipped',
    })
    return { violations, warnings, cached: 0, probed: 0, checked: 0, exempted }
  }

  const cache = readCache(cacheFile)
  const now = Date.now()
  const deadline = now + budgetMs

  const record = (entry: ClassifiedUrl, probe: CachedProbe): void => {
    if (probe.verdict === 'ok') return
    if (probe.verdict === 'html') {
      violations.push({
        url: entry.url,
        source: entry.source,
        from: entry.from,
        reason:
          `resolved 200 with content-type "${probe.contentType}" — an HTML page where a ` +
          `non-HTML asset was promised. This is the "worse than a 404" shape: nothing errors, ` +
          `nothing logs, and every crawler resolving it silently gets a web page`,
      })
      return
    }
    violations.push({
      url: entry.url,
      source: entry.source,
      from: entry.from,
      reason: `resolved HTTP ${probe.status} — the URL the factory published does not exist`,
    })
  }

  const queue = [...targets]
  const worker = async (): Promise<void> => {
    for (;;) {
      const entry = queue.shift()
      if (!entry) return

      const cached = cache.entries[entry.url]
      if (cached && now - cached.at < cacheTtlMs) {
        cachedCount += 1
        answeredCount += 1
        record(entry, cached)
        continue
      }

      if (Date.now() >= deadline) {
        warnings.push({
          url: entry.url,
          source: entry.source,
          from: entry.from,
          reason: `remote-probe time budget (${budgetMs}ms) exhausted before this URL was checked`,
        })
        continue
      }

      probedCount += 1
      let res: Response | undefined
      try {
        res = await fetchImpl(entry.url, {
          method: 'HEAD',
          redirect: 'follow',
          signal: AbortSignal.timeout(timeoutMs),
        })
        // Some CDNs and SWA reject HEAD; fall back to a ranged GET so we still
        // learn the content type without downloading the whole asset.
        if (res.status === 405 || res.status === 501) {
          res = await fetchImpl(entry.url, {
            method: 'GET',
            redirect: 'follow',
            headers: { Range: 'bytes=0-2047' },
            signal: AbortSignal.timeout(timeoutMs),
          })
        }
      } catch (err) {
        warnings.push({
          url: entry.url,
          source: entry.source,
          from: entry.from,
          reason: `network probe did not complete (${
            err instanceof Error ? err.message : String(err)
          }) — treated as UNKNOWN, not as a failure`,
        })
        continue
      }

      const contentType = res.headers.get('content-type') ?? undefined
      let probe: CachedProbe
      if (res.status >= 400) {
        probe = { verdict: 'status', status: res.status, contentType, at: Date.now() }
      } else if (isHtmlContentType(contentType)) {
        probe = { verdict: 'html', status: res.status, contentType, at: Date.now() }
      } else {
        probe = { verdict: 'ok', status: res.status, contentType, at: Date.now() }
      }
      cache.entries[entry.url] = probe
      answeredCount += 1
      if (debug) {
        console.log(`[dcs-seo] url probe ${entry.url} -> ${probe.verdict} (${res.status} ${contentType ?? '?'})`)
      }
      record(entry, probe)
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker))
  writeCache(cacheFile, cache)

  // `checked` counts only URLs that got a definitive answer — a budget-exhausted
  // or DNS-failed probe is an UNKNOWN and must never be counted as verified.
  return {
    violations,
    warnings,
    cached: cachedCount,
    probed: probedCount,
    checked: answeredCount,
    exempted,
  }
}

// -----------------------------------------------------------------------------
// Reporting
// -----------------------------------------------------------------------------

/** Render the failure report — every violation names the URL, where it was
 * emitted, and what actually came back. */
export function formatEmittedUrlReport(violations: EmittedUrlViolation[]): string {
  const lines: string[] = [
    `[dcs-seo] emitted-URL honesty: ${violations.length} URL(s) the SEO factory publishes do not ` +
      `resolve to what they promise.`,
  ]
  for (const v of violations) {
    lines.push(`  ${v.url}`)
    lines.push(`      emitted by: ${v.source}  (in ${v.from})`)
    lines.push(`      problem   : ${v.reason}`)
  }
  lines.push(
    `  Fix: point the URL at something that exists (via .dcs/seo.yaml — brand marks usually ` +
      `live on the per-site CDN), or ship the file into the site's public/ directory.`
  )
  return lines.join('\n')
}

// -----------------------------------------------------------------------------
// Execution + claim honesty (C-338)
// -----------------------------------------------------------------------------

/**
 * Exactly what the P2 pass touched — the numbers the build log is allowed to
 * claim.
 *
 * ## The defect this exists for
 *
 * C-334's success line printed `${classified.length} published URL(s) verified`
 * while BOTH checkers filter `expectation !== 'asset'`. Sitemap `<loc>`s,
 * canonicals and llms.txt page links were counted as verified and never probed —
 * the claim was broader than the check, which is the same vacuous-green shape
 * the rail was built to close. The log now reports these fields verbatim:
 * assets proven / assets probed / pages COLLECTED-BUT-NOT-VERIFIED / unknown.
 */
export interface EmittedUrlAudit {
  /** Every distinct URL collected from the emitted artifacts. */
  collected: number
  /** Same-origin asset URLs resolved against `dist/` (offline, exact). */
  sameOriginAssetsChecked: number
  /** Remote asset URLs that got a definitive answer. */
  remoteAssetsChecked: number
  /** Remote asset probes that hit the network / came from cache. */
  remoteProbed: number
  remoteCached: number
  /**
   * Remote asset URLs the pass could NOT get a definitive answer about (DNS,
   * timeout, budget) plus every remote asset when probing is disabled. Never
   * counted as verified.
   */
  unknown: number
  /**
   * Page URLs collected and classified but NOT verified by this rail. This is
   * the honest name for what used to be counted as "verified".
   */
  pagesUnverified: number
  /** URLs that are not fetchable by construction (`data:`, `mailto:`, `tel:`). */
  unfetchable: number
  /** URLs skipped by an explicit allow-list entry (still logged). */
  exempted: number
  /** Violations found (the failures). */
  failed: number
}

/** Total URLs this pass actually verified. */
export function auditCheckedTotal(audit: EmittedUrlAudit): number {
  return audit.sameOriginAssetsChecked + audit.remoteAssetsChecked
}

/**
 * The success/summary line. It states the denominator of every claim and names
 * what was NOT checked, so no reader can infer more coverage than exists.
 */
export function formatEmittedUrlAudit(audit: EmittedUrlAudit, routeCount: number): string {
  const checked = auditCheckedTotal(audit)
  const parts = [
    `[dcs-seo] emitted-URL honesty: ${checked} asset URL(s) VERIFIED ` +
      `(${audit.sameOriginAssetsChecked} proven against dist/, ${audit.remoteAssetsChecked} probed remotely` +
      `${audit.remoteCached ? `, ${audit.remoteCached} from cache` : ''})`,
    `${audit.pagesUnverified} page URL(s) collected but NOT verified by this rail`,
  ]
  if (audit.unknown > 0) parts.push(`${audit.unknown} UNKNOWN (no definitive answer)`)
  if (audit.exempted > 0) parts.push(`${audit.exempted} exempted`)
  if (audit.unfetchable > 0) parts.push(`${audit.unfetchable} unfetchable by construction`)
  return (
    parts.join('; ') +
    `. ${audit.collected} URL(s) collected across ${routeCount} route(s) + sitemap + llms.txt.`
  )
}

/** Thrown in `error` mode when the P2 rail collected nothing to check. */
export class EmittedUrlNotRunError extends Error {
  readonly audit: EmittedUrlAudit
  constructor(audit: EmittedUrlAudit, detail: string) {
    super(
      '[dcs-seo] emitted-URL honesty DID NOT RUN — ' +
        `${detail}. Nothing was verified, so this build proves nothing about the URLs it ` +
        'publishes. (Emitted artifacts unreadable or empty? outDir wrong?) A rail that ' +
        'silently does not run is the exact class this work exists to close.'
    )
    this.name = 'EmittedUrlNotRunError'
    this.audit = audit
  }
}

/** Thrown in `error` mode so the build goes red. */
export class EmittedUrlError extends Error {
  readonly violations: EmittedUrlViolation[]
  constructor(violations: EmittedUrlViolation[]) {
    super(formatEmittedUrlReport(violations))
    this.name = 'EmittedUrlError'
    this.violations = violations
  }
}
